import {
	splitRecordViewPath,
	viewPathSuffix,
} from "@/components/views/views-data";
import { parseAppHref } from "@/lib/activity-links";
import { eventPageHref, parseEventPath } from "@/lib/event-path";
import { isReservedOwnerSegment } from "@/lib/reserved";

/**
 * Search keys owned by the right-side peek pane. Target fields are namespaced
 * so opening and closing a preview cannot overwrite the current route's own
 * `view`, `run_ids`, `agent`, or `automation` filters.
 *
 * `peek_view` is which tab of the peeked subject is showing: beside a single
 * `peek_content_ids` event the key of a view that renders that event (absent:
 * the event itself), and beside `peek_entity` the key of one of the record's
 * views (absent: its activity), and beside `peek_entity_type` `activity` for
 * what has happened across the type's records (absent: the type itself, its
 * editor or fields). The automation peek is always its activity — editing is
 * the page the row opens — so a legacy `peek_view=definition` is ignored.
 * `peek_agent` / `peek_client_ids` are one agent's or connected app's activity.
 */
export const PEEK_SEARCH_KEYS = [
	"peek",
	"peek_run_id",
	"peek_content_ids",
	"peek_automation",
	"peek_view",
	"peek_entity",
	"peek_entity_type",
	"peek_agent",
	"peek_client_ids",
] as const;

type PeekSearchKey = (typeof PEEK_SEARCH_KEYS)[number];
type PeekSearch = Partial<Record<PeekSearchKey, string>>;

function isPeekOpenFlag(value: unknown): boolean {
	return value === "1" || value === "true" || value === true || value === 1;
}

export function pickPeekSearch(
	search: Record<string, unknown> | undefined | null,
): PeekSearch {
	if (!search) return {};
	const out: PeekSearch = {};
	for (const key of PEEK_SEARCH_KEYS) {
		const value = search[key];
		if (value == null || value === "") continue;
		if (key === "peek") {
			if (isPeekOpenFlag(value)) out.peek = "1";
			continue;
		}
		if (typeof value === "string" && value.trim()) {
			out[key] = value.trim();
		} else if (typeof value === "number" && Number.isFinite(value)) {
			out[key] = String(value);
		}
	}
	return out;
}

export function omitPeekSearch<T extends Record<string, unknown>>(
	search: T,
): Omit<T, PeekSearchKey> {
	const next = { ...search };
	for (const key of PEEK_SEARCH_KEYS) delete next[key];
	return next as Omit<T, PeekSearchKey>;
}

export function hasPeekFlag(
	search: Record<string, unknown> | undefined,
): boolean {
	return isPeekOpenFlag(search?.peek);
}

/**
 * The record a bare `?peek=1` means: the one the current path names.
 *
 * On a record's own page the path already says which record it is, so repeating
 * it as `peek_entity=person%2Falice` is noise in the URL bar. `?peek=1` there
 * means "this record"; the explicit key stays for peeks opened from anywhere
 * else (a list row, a notification), where the path names something different.
 */
export function implicitPeekEntityPath(pathname: string): string | null {
	const segments = pathname.split("/").filter(Boolean);
	if (segments.length < 3 || segments.length % 2 === 0) return null;
	if (isReservedOwnerSegment(segments[1])) return null;
	const path = segments.slice(1).join("/");
	return isEntityPath(path) ? path : null;
}

/** Convert namespaced peek state into the destination href rendered by the pane. */
export function destinationHrefFromPeekSearch(
	owner: string,
	search: PeekSearch,
): string | null {
	if (search.peek_automation) {
		const query = new URLSearchParams();
		if (search.peek_view === "activity" || search.peek_view === "definition") {
			query.set("view", search.peek_view);
		}
		if (search.peek_run_id && /^\d+$/.test(search.peek_run_id)) {
			query.set("run_id", search.peek_run_id);
		}
		const suffix = query.size > 0 ? `?${query.toString()}` : "";
		return `/${owner}/automations/${search.peek_automation}${suffix}`;
	}

	if (search.peek_run_id && isIdList(search.peek_run_id)) {
		const query = new URLSearchParams({
			run_ids: search.peek_run_id,
		});
		return `/${owner}/events?${query.toString()}`;
	}

	if (search.peek_content_ids && isIdList(search.peek_content_ids)) {
		// One event is a subject with a page of its own (and its views); several
		// are a filter of the log.
		const ids = search.peek_content_ids.split(",");
		if (ids.length === 1) {
			const single = eventPageHref(owner, Number(ids[0].trim()));
			const viewed = search.peek_view
				? eventPageHref(owner, Number(ids[0].trim()), search.peek_view)
				: single;
			return parseEventPath(viewed) ? viewed : single;
		}
		const query = new URLSearchParams({
			content_ids: search.peek_content_ids,
		});
		return `/${owner}/events?${query.toString()}`;
	}

	// A record's view opens fullscreen in the peek; its full page is the
	// record's own view path.
	if (
		search.peek_entity &&
		isEntityPath(search.peek_entity) &&
		search.peek_view
	) {
		return `/${owner}/${search.peek_entity}${viewPathSuffix(search.peek_view)}`;
	}

	// Record activity uses the same path in the peek and its full-width page.
	if (search.peek_entity && isEntityPath(search.peek_entity)) {
		return `/${owner}/events/${search.peek_entity}`;
	}

	if (search.peek_agent) {
		return `/${owner}/events?${new URLSearchParams({ agent: search.peek_agent })}`;
	}
	if (search.peek_client_ids) {
		return `/${owner}/events?${new URLSearchParams({ client_ids: search.peek_client_ids })}`;
	}

	if (search.peek_entity_type && isEntityTypeSegment(search.peek_entity_type)) {
		return entityTypePeekHref(
			owner,
			search.peek_entity_type,
			search.peek_view === "activity" ? "activity" : undefined,
		);
	}

	return null;
}

/**
 * One entity type's peek. Its full page is the Activity page filtered to the
 * type, which ignores `view`; inside the pane `view=activity` shows that feed
 * instead of the type itself.
 */
export function entityTypePeekHref(
	owner: string,
	typeSlug: string,
	view?: "activity",
): string {
	const query = new URLSearchParams({ type: typeSlug });
	if (view) query.set("view", view);
	return `/${owner}/events?${query.toString()}`;
}

/** Convert a peekable destination href into namespaced current-route search. */
export function peekSearchFromDestinationHref(href: string): PeekSearch | null {
	// Absolute URLs to other origins (a TikTok link is `/@handle/video/<id>`,
	// an entity-shaped path) are external destinations, never peek subjects.
	// Without this guard they classify as entity paths and open phantom
	// record peeks that can only miss. Outside a browser there is no app
	// origin to compare against, so every absolute URL counts as external.
	const appOrigin =
		typeof window !== "undefined" ? window.location.origin : null;
	if (URL.canParse(href)) {
		const absolute = new URL(href);
		if (
			(absolute.protocol === "http:" || absolute.protocol === "https:") &&
			absolute.origin !== appOrigin
		) {
			return null;
		}
	}
	const { pathname, search } = parseAppHref(href);
	const segments = pathname.split("/").filter(Boolean);

	if (
		segments.length === 2 &&
		(segments[1] === "events" || segments[1] === "memory")
	) {
		const runIds = search?.run_ids;
		if (runIds && isIdList(runIds)) {
			return { peek: "1", peek_run_id: runIds };
		}
		const contentIds = search?.content_ids;
		if (contentIds && isIdList(contentIds)) {
			return { peek: "1", peek_content_ids: contentIds };
		}
		// One entity type — what a schema-map box and the type page's Edit open.
		const type = search?.type?.trim();
		if (type && isEntityTypeSegment(type)) {
			return search?.view === "activity"
				? { peek: "1", peek_entity_type: type, peek_view: "activity" }
				: { peek: "1", peek_entity_type: type };
		}
		const agent = agentScope(search);
		if (agent)
			return "agentId" in agent
				? { peek: "1", peek_agent: agent.agentId }
				: { peek: "1", peek_client_ids: agent.clientIds.join(",") };
		return null;
	}

	// An event's own page, or one of the views that render it.
	const event = parseEventPath(pathname);
	if (event) {
		return {
			peek: "1",
			peek_content_ids: String(event.eventId),
			...(event.viewKey ? { peek_view: event.viewKey } : {}),
		};
	}

	// Both forms of a record peek: its own page (what "See activity" is clicked
	// from) and its full-width activity route (what the pane links out to).
	if (segments.length >= 4 && segments[1] === "events") {
		const path = segments.slice(2).join("/");
		return isEntityPath(path) ? { peek: "1", peek_entity: path } : null;
	}

	if (segments.length >= 3 && !isReservedOwnerSegment(segments[1])) {
		const split = splitRecordViewPath(segments.slice(1).join("/"));
		if (!split || !isEntityPath(split.entityPath)) return null;
		return split.viewKey
			? { peek: "1", peek_entity: split.entityPath, peek_view: split.viewKey }
			: { peek: "1", peek_entity: split.entityPath };
	}

	if (segments.length === 3 && segments[1] === "automations") {
		const out: PeekSearch = {
			peek: "1",
			peek_automation: segments[2],
		};
		if (search?.view === "activity" || search?.view === "definition") {
			out.peek_view = search.view;
		}
		if (search?.run_id && /^\d+$/.test(search.run_id)) {
			out.peek_run_id = search.run_id;
		}
		return out;
	}

	return null;
}

/**
 * `<type>/<slug>[/<type>/<slug>…]` — the entity path form, pair-wise so a
 * half-written path can never address a record. Segments stay percent-encoded
 * (a reserved `$member` type arrives as `%24member`).
 */
export function isEntityPath(value: string): boolean {
	const segments = value.split("/");
	return (
		segments.length >= 2 &&
		segments.length % 2 === 0 &&
		segments.every((segment) => segment.length > 0 && !segment.includes("?")) &&
		isEntityTypeSegment(segments[0])
	);
}

/**
 * An entity type slug, as a path segment (still percent-encoded, `%24member`)
 * or as a search value (`$member`).
 */
export function isEntityTypeSegment(value: string): boolean {
	return (
		value.length > 0 &&
		!value.includes("/") &&
		!value.includes("?") &&
		!isReservedOwnerSegment(value) &&
		!["chat", "entity-types", "recent"].includes(value)
	);
}

/**
 * An agent's or app's whole activity, and nothing narrower: a conversation
 * drill (`mcp_activity_id`) or any other filter beside it stays a filtered page.
 */
export function agentScope(
	search: Record<string, string> | undefined,
): { agentId: string } | { clientIds: string[] } | null {
	const keys = Object.keys(search ?? {});
	if (keys.length !== 1) return null;
	const agentId = search?.agent?.trim();
	if (agentId) return { agentId };
	const clientIds = (search?.client_ids ?? "")
		.split(",")
		.map((id) => id.trim())
		.filter(Boolean);
	return clientIds.length > 0 && clientIds.length <= 50 ? { clientIds } : null;
}

function isIdList(value: string): boolean {
	const ids = value.split(",");
	return (
		ids.length > 0 &&
		ids.length <= 50 &&
		ids.every((id) => /^\d+$/.test(id.trim()) && Number(id) > 0)
	);
}

export function mergePeekSearch(
	current: Record<string, unknown> | undefined,
	peek: PeekSearch,
): Record<string, unknown> {
	const base: Record<string, unknown> = { ...current };
	for (const key of PEEK_SEARCH_KEYS) delete base[key];
	for (const [key, value] of Object.entries(peek)) {
		if (value) base[key] = value;
	}
	return base;
}
